import React from 'react';
import { MoonIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import type { MembershipId, MembershipTier } from '../types/copy';
interface MembershipsProps {
  onSelect: (id: MembershipId) => void;
}
export function Memberships({
  onSelect
}: MembershipsProps) {
  const {
    t
  } = useLanguage();
  const m = t.memberships;
  return <section id="memberships" aria-labelledby="memberships-title" className="scroll-mt-20 bg-cream py-20 md:py-32" data-id="element-217" data-mp="3pkbkjqwpqko4">
      <div className="mx-auto max-w-7xl px-5 md:px-10" data-id="element-218" data-mp="3ujxu9qdc1is6">
        <header className="mx-auto max-w-3xl text-center" data-id="element-219" data-mp="3d4wyxlnu3sx">
          <h2 id="memberships-title" className="font-display text-[30px] font-semibold uppercase tracking-[0.12em] text-ink md:text-[38px]" data-id="element-220" data-mp="3bk37a766ow4">
            {m.title}
          </h2>
          <p className="mt-5 font-serif text-[18px] italic leading-[1.6] text-muted md:text-[20px]" data-id="element-221" data-mp="36rr63ehrgi3u">
            {m.intro}
          </p>
        </header>

        <div className="mt-14 grid items-stretch gap-8 lg:grid-cols-2 lg:gap-10" data-id="element-222" data-mp="2a59360ak33vj">
          {m.tiers.map(tier => <TierCard key={tier.id} tier={tier} featured={tier.id === 'signature'} cta={m.getStarted} onSelect={() => onSelect(tier.id)} data-id="element-223" data-mp="257pox0duyr3l" />)}
        </div>

        <div className="mt-14 text-center" data-id="element-224" data-mp="1rfjb3ky6gw7b">
          <p className="text-[16px] text-muted" data-id="element-225" data-mp="1rhdv8x1mkkac">{m.footer}</p>
          <p className="mt-2 text-[13px] font-medium tracking-[0.08em] text-taupe" data-id="element-226" data-mp="1mg6lwtju3d1k">{m.byline}</p>
          <a href="#contact" className="btn btn-outline mt-6" data-id="element-227" data-mp="1hh3g36w152sj">
            {m.contactCta}
          </a>
        </div>
      </div>
    </section>;
}
function TierCard({
  tier,
  featured,
  cta,
  onSelect
}: {
  tier: MembershipTier;
  featured: boolean;
  cta: string;
  onSelect: () => void;
}) {
  const Icon = featured ? MoonIcon : SparklesIcon;
  return <article aria-labelledby={`tier-${tier.id}`} className={`flex flex-col rounded-[24px] p-7 transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] hover:-translate-y-1 md:p-12 ${featured ? 'on-dark bg-charcoal text-cream shadow-[0_30px_60px_-36px_rgba(17,24,39,0.6)] hover:shadow-[0_36px_70px_-34px_rgba(17,24,39,0.7)]' : 'border border-mist bg-white text-charcoal hover:shadow-[0_28px_48px_-30px_rgba(30,27,75,0.35)]'}`} data-id="element-228" data-mp="36ugsljeapx4q">
      <div className="flex items-center gap-3" data-id="element-229" data-mp="1nw2cmb2jffqs">
        <Icon aria-hidden="true" className={`h-6 w-6 ${featured ? 'text-violet-light' : 'text-violet'}`} data-id="element-230" data-mp="1svx3iembc5bu" />
        <h3 id={`tier-${tier.id}`} className={`font-display text-[24px] font-semibold uppercase tracking-[0.08em] md:text-[26px] ${featured ? 'text-white' : 'text-ink'}`} data-id="element-231" data-mp="1ewf7be6otpsg">
          {tier.name}
        </h3>
      </div>
      <p className={`mt-2 text-[13px] font-semibold uppercase tracking-[0.14em] ${featured ? 'text-violet-light' : 'text-violet'}`} data-id="element-232" data-mp="7k533l8rbl8s">
        {tier.term}
      </p>
      <p className={`mt-5 text-[16px] leading-[1.7] ${featured ? 'text-cream/80' : 'text-muted'}`} data-id="element-233" data-mp="3r6x92cr912b6">
        {tier.description}
      </p>

      {tier.groups.map(group => <div key={group.title} className={`mt-8 border-t pt-6 ${featured ? 'border-white/10' : 'border-mist'}`} data-id="element-234" data-mp="4z7euyvzxjh9">
          <h4 className={`text-[13px] font-semibold uppercase tracking-[0.12em] ${featured ? 'text-white' : 'text-ink'}`} data-id="element-235" data-mp="3dg259evlxbeg">
            {group.title}
          </h4>
          <ul className="mt-4 space-y-2.5" data-id="element-236" data-mp="3n6dar5uxvsng">
            {group.items.map(item => <li key={item.text} className={`flex gap-3 text-[15px] leading-[1.7] ${featured ? 'text-cream/85' : 'text-charcoal'}`} data-id="element-237" data-mp="3idbsqozfrczn">
                <span aria-hidden="true" className={`mt-[10px] h-1.5 w-1.5 shrink-0 rotate-45 ${featured ? 'bg-violet-light' : 'bg-violet'}`} data-id="element-238" data-mp="2pq7vx7ahy4yy" />
                <span data-id="element-239" data-mp="1wzwdbm9sxm2k">
                  {item.label && <strong className={`font-semibold ${featured ? 'text-white' : 'text-ink'}`} data-id="element-240" data-mp="21yhdxmsyu7rj">
                      {item.label}{' '}
                    </strong>}
                  {item.text}
                </span>
              </li>)}
          </ul>
        </div>)}

      <div className="mt-auto pt-10" data-id="element-241" data-mp="2gmuaakvytsmk">
        <button type="button" onClick={onSelect} className={`btn w-full sm:w-auto ${featured ? 'bg-cream text-ink hover:bg-white' : 'btn-primary'}`} data-id="element-242" data-mp="2glss7wgj85vc">
          {cta}
          <span className="sr-only" data-id="element-243" data-mp="2pm7mvmfdyxbi">: {tier.name}</span>
        </button>
      </div>
    </article>;
}